import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import atlanticBadge from '@/assets/images/atlantic-health-mini.png';
import placeholder from '@/assets/images/Doctor_Placeholder@2x.png';
import { Breadcrumb } from '@/design-system/atoms/Breadcrumb';
import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { LazyIconCalendar } from '@/design-system/atoms/Icons/IconCalendar/Lazy';
import { LazyIconCheck } from '@/design-system/atoms/Icons/IconCheck/Lazy';
import { LazyIconPhone } from '@/design-system/atoms/Icons/IconPhone/Lazy';
import { LazyIconVideo } from '@/design-system/atoms/Icons/IconVideo/Lazy';
import { Image } from '@/design-system/atoms/Image';
import { StarRatings } from '@/design-system/atoms/StarRatings';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { usePhysicianBioAvailabilityDate } from '@/hooks/usePhysicianBioAvailabilityDate';

import styles from './PhysicianBioHero.module.scss';

/** Availability copy for a Virtual Care slot inside the "now" window. */
const AVAILABLE_NOW_TEXT = 'Available Now';

/** Label rendered beside the olive video icon in the header row. */
const VIRTUAL_CARE_INDICATOR_TEXT = 'Virtual Care';

/** Schedule CTA label for a Virtual Care provider, in every availability category. */
const VIRTUAL_CARE_CTA_TEXT = 'Schedule Your Virtual Visit';

const formatAvailabilityDate = (date) => (date ? moment(date).format('ddd MMM DD') : '');

/**
 * Resolves the appointment block's availability message.
 *
 * `onlineBooking` and `availableSoonOnline` are both master gates: when either is false
 * this returns early with `null` without consulting `hasVirtualCare`, `category`, or
 * either date, so no promissory availability copy renders for a provider whose
 * scheduling is not enabled or who has no near-term availability. Beneath those gates,
 * every provider (Virtual Care or not) reaches the same tiered `category` switch — a
 * time when available today, a date when available a future day, nothing when
 * `category` is `"none"`. `hasVirtualCare` only selects the `"now"` bucket's wording:
 * "Available Now" for Virtual Care, the actual time for everyone else.
 *
 * @param {Object} params
 * @param {boolean} params.availableSoonOnline Whether the provider has availability soon
 * @param {boolean} params.onlineBooking Whether online scheduling is enabled.
 * @param {boolean} params.hasVirtualCare Whether the provider offers Virtual Care.
 * @param {('now'|'soon-time'|'soon-day'|'none')} params.category Availability category
 *     derived by `usePhysicianBioAvailabilityDate`.
 * @param {?string} params.nextAvailableTime Formatted next slot time, e.g. `'3:30pm'`.
 * @param {?string} params.availabilityDate Next available date as `YYYY-MM-DD`.
 * @param {string} params.availabilityText Authored label that prefixes a date/time.
 * @returns {?{prefix: ?string, value: string, valueVariant: string}} The message's
 *     optional prefix, its value, and the `Text` variant that value renders at, or
 *     `null` when no availability message renders. `"Available Now"` is a standalone
 *     statement rather than a date/time value, so per the design it renders one weight
 *     lighter than the emphasized `soon-time`/`soon-day` values.
 */
const getAvailabilityMessage = ({
    availableSoonOnline,
    onlineBooking,
    hasVirtualCare,
    category,
    nextAvailableTime,
    availabilityDate,
    availabilityText,
}) => {
    if (!onlineBooking || !availableSoonOnline) {
        return null;
    }

    switch (category) {
        case 'now':
            // "Available Now" is Virtual-Care-only; a non-virtual provider available
            // within the hour still shows the time, not the "now" wording.
            return hasVirtualCare
                ? { prefix: null, value: AVAILABLE_NOW_TEXT, valueVariant: Text.VARIANT.T2 }
                : nextAvailableTime
                  ? {
                        prefix: availabilityText,
                        value: nextAvailableTime,
                        valueVariant: Text.VARIANT.T2B,
                    }
                  : null;
        case 'soon-time':
            return {
                prefix: availabilityText,
                value: nextAvailableTime,
                valueVariant: Text.VARIANT.T2B,
            };
        case 'soon-day':
            return availabilityDate
                ? {
                      prefix: availabilityText,
                      value: formatAvailabilityDate(availabilityDate),
                      valueVariant: Text.VARIANT.T2B,
                  }
                : null;
        default:
            return null;
    }
};

const PhysicianBioHero = ({
    id,
    breadcrumbs,
    providerImage,
    acceptingNewPatients,
    onlineBooking,
    hasVirtualCare = false,
    additionalVisitTypes,
    providerName,
    primarySpecialty,
    specialties,
    averageRating,
    numberOfReviews,
    reviewsLink = '#reviews',
    affiliationBadgeLabel,
    languages,
    gender,
    lgbtServices,
    insurancesText = 'Want to know your insurance options?',
    insurancesLinkText = 'See Full List',
    insurancesAriaLabel = 'See full list of Insurances',
    insuranceLink,
    bookAppointmentText = 'Book Appointment',
    bookAppointmentAriaLabel,
    availabilityText = 'Available as soon as: ',
    npi,
    newPatientAppointmentIds,
    departmentIds,
    appointmentUrl,
    phoneNumber,
    // AHSMKTG-3534: New availability model props
    availableSoonOnline = false,
    availableOnline = false,
    firstAvailabilityDate = null,
    nextAvailabilityDate = null,
    disableAvailabilityMessage = false,
    ...props
}) => {
    const physicianBioHeightRef = useRef();
    const physicianBioBgRef = useRef();
    const physicianBioContainerRef = useRef();

    const [bgHeight, setBgHeight] = useState(0);

    const { availabilityDate, category, nextAvailableTime } = usePhysicianBioAvailabilityDate({
        npi,
        newPatientAppointmentIds,
        departmentIds,
        firstAvailabilityDate,
        nextAvailabilityDate,
    });

    const availabilityMessage = getAvailabilityMessage({
        availableSoonOnline,
        onlineBooking,
        hasVirtualCare,
        category,
        nextAvailableTime,
        availabilityDate,
        availabilityText,
    });

    const scheduleCtaLabel = hasVirtualCare ? VIRTUAL_CARE_CTA_TEXT : bookAppointmentText;

    /*
     * An authored `bookAppointmentAriaLabel` always wins. The fallback is derived from
     * whichever label is actually visible — the internal Virtual Care swap or an
     * authored `bookAppointmentText` — so the visible label is always contained in the
     * accessible name (WCAG 2.5.3).
     */
    const scheduleCtaAriaLabel =
        bookAppointmentAriaLabel || `${scheduleCtaLabel} with ${providerName}`;

    // AHSMKTG-3534: Has online availability per the backend-computed model (feeds the
    // Book Appointment CTA / phone button gating below) — independent of the
    // calendar-derived `category` that drives `availabilityMessage` above.
    const hasOnlineAvailability = availableSoonOnline || availableOnline;

    const ImageComponent = providerImage ? (
        <Image
            {...providerImage}
            fallbackSrc={placeholder}
            alt={providerName}
            lazyEnabled={false}
            loading="eager"
            fetchpriority="high"
        />
    ) : (
        <Image
            src={placeholder}
            alt="Doctor"
            lazyEnabled={false}
            loading="eager"
            fetchpriority="high"
        />
    );

    const filteredLanguages = languages?.filter((language) => language !== 'English');

    let renderedSpecialties = [];
    let filteredSpecialties = specialties;

    if (primarySpecialty) {
        renderedSpecialties.push(primarySpecialty);
        if (Array.isArray(specialties)) {
            filteredSpecialties = specialties.filter((specialty) => specialty !== primarySpecialty);
        }
    }
    const otherSpecialties = filteredSpecialties?.slice(0, primarySpecialty ? 2 : 3);
    const restSpecialties = filteredSpecialties?.slice(primarySpecialty ? 2 : 3);

    if (otherSpecialties?.length > 0) {
        for (const specialty of otherSpecialties) {
            renderedSpecialties.push(specialty);
        }
    }
    const remainderSpecialities =
        restSpecialties?.length > 0 ? `+ ${restSpecialties.length} more` : null;

    const analyticsSpecialties =
        renderedSpecialties?.length > 0
            ? `${renderedSpecialties.join(', ')}${remainderSpecialities || ''}`
            : '';

    useEffect(() => {
        const containerHeight = physicianBioHeightRef?.current.clientHeight;
        const paddingAmount = parseInt(
            window
                .getComputedStyle(physicianBioContainerRef?.current)
                .getPropertyValue('padding-top'),
        );
        const totalHeight = containerHeight / 2 + paddingAmount;
        setBgHeight(totalHeight);
    }, []);

    return (
        <>
            <section
                className={styles[`physician-bio-hero`]}
                ref={physicianBioHeightRef}
                id={id}
                {...props}
            >
                <div
                    className={styles[`physician-bio-hero__bg`]}
                    ref={physicianBioBgRef}
                    style={{ '--height': `${bgHeight}px` }}
                >
                    <Theme
                        name={Theme.NAME.PROVIDERS}
                        className={styles[`physician-bio-hero__theme`]}
                    >
                        <></>
                    </Theme>
                </div>

                <ContentContainer
                    className={styles[`physician-bio-hero__container`]}
                    ref={physicianBioContainerRef}
                >
                    {breadcrumbs && (
                        <Breadcrumb
                            items={breadcrumbs}
                            className={styles[`physician-bio-hero__breadcrumb`]}
                        />
                    )}
                    <Grid
                        col={{ sm: 4, md: 6, lg: 12 }}
                        row={{ sm: 3, md: 3, lg: 2 }}
                        className={styles[`physician-bio-hero__grid`]}
                    >
                        <GridColumn
                            colSpan={{ sm: 2, md: 2, lg: 3 }}
                            colStart={{ sm: 1, md: 1, lg: 1 }}
                            rowStart={{ sm: 1, md: 1, lg: 1 }}
                            rowSpan={{ sm: 1, md: 2, lg: 2 }}
                            className={styles[`physician-bio-hero__image`]}
                        >
                            {ImageComponent}
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 2, md: 4, lg: 5 }}
                            colStart={{ sm: 3, md: 3, lg: 4 }}
                            rowSpan={{ sm: 1, md: 1, lg: 1 }}
                            rowStart={{ sm: 1, md: 1, lg: 1 }}
                            className={styles[`physician-bio-hero__patient-info`]}
                        >
                            {acceptingNewPatients && (
                                <Text
                                    content="Accepting New Patients"
                                    tag={Text.TAG.P}
                                    variant={Text.VARIANT.T4}
                                    className={styles[`physician-bio-hero__accepting-badge`]}
                                />
                            )}
                            <div className={styles[`physician-bio-hero__visit-container`]}>
                                {hasVirtualCare && (
                                    <div className={styles[`physician-bio-hero__visit`]}>
                                        <LazyIconVideo />
                                        <Text
                                            content={VIRTUAL_CARE_INDICATOR_TEXT}
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                            className={styles[`physician-bio-hero__visit-type`]}
                                        />
                                    </div>
                                )}
                                {additionalVisitTypes &&
                                    Array.isArray(additionalVisitTypes) &&
                                    additionalVisitTypes.map((type, index) => (
                                        <div
                                            className={styles[`physician-bio-hero__visit`]}
                                            key={index}
                                        >
                                            <LazyIconCheck />
                                            <Text
                                                content={type}
                                                tag={Text.TAG.P}
                                                variant={Text.VARIANT.T3}
                                                className={styles[`physician-bio-hero__visit-type`]}
                                            />
                                        </div>
                                    ))}
                            </div>
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 4, md: 4, lg: 5 }}
                            colStart={{ sm: 1, md: 3, lg: 4 }}
                            rowSpan={{ sm: 1, md: 1, lg: 1 }}
                            rowStart={{ sm: 2, md: 2, lg: 2 }}
                            className={styles[`physician-bio-hero__info`]}
                        >
                            <Title
                                content={providerName}
                                tag={Title.TAG.H1}
                                variant={Title.VARIANT.H3}
                                className={`${styles['physician-bio-hero__title']} GlobalLinkNoTx`}
                            />
                            {renderedSpecialties?.length > 0 && (
                                <div
                                    className={styles[`physician-bio-hero__specialties-container`]}
                                >
                                    <Text
                                        content={renderedSpecialties.join(', ')}
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T2}
                                        className={styles[`physician-bio-hero__specialties`]}
                                    />
                                    {remainderSpecialities && (
                                        <Button
                                            label={remainderSpecialities}
                                            buttonStyle={Button.STYLE.TEXT_SMALL}
                                            className={
                                                styles[`physician-bio-hero__specialties-more`]
                                            }
                                            href="#about"
                                        />
                                    )}
                                </div>
                            )}
                            <div className={styles[`physician-bio-hero__footer`]}>
                                {typeof averageRating === 'number' && averageRating > 0 && (
                                    <div className={styles[`physician-bio-hero__rating-container`]}>
                                        <StarRatings ratings={averageRating} total={5} />
                                        <Text
                                            content={averageRating?.toFixed(1)}
                                            tag={Text.TAG.SPAN}
                                            variant={Text.VARIANT.T3}
                                            className={styles[`physician-bio-hero__rating-text`]}
                                        />
                                        <div
                                            className={
                                                styles[`physician-bio-hero__reviews-container`]
                                            }
                                        >
                                            <Text
                                                content={`(`}
                                                tag={Text.TAG.SPAN}
                                                variant={Text.VARIANT.T3}
                                            />
                                            <Button
                                                buttonStyle={Button.STYLE.TEXT_SMALL}
                                                label={String(numberOfReviews)}
                                                href={reviewsLink}
                                                className={styles[`physician-bio-hero__reviews`]}
                                                analytics={{
                                                    componentName: 'fad_bio_hero',
                                                    actionLabel: 'reviews',
                                                    acceptingNewPatients: acceptingNewPatients,
                                                    providerName: providerName,
                                                    providerSpecialties: analyticsSpecialties,
                                                    providerRating: averageRating,
                                                    interactionType: 'fad_bio_cta',
                                                }}
                                            />
                                            <Text
                                                content={`)`}
                                                tag={Text.TAG.SPAN}
                                                variant={Text.VARIANT.T3}
                                            />
                                        </div>
                                    </div>
                                )}
                                {affiliationBadgeLabel !== '' && (
                                    <div className={styles[`physician-bio-hero__affiliation`]}>
                                        <Image
                                            src={atlanticBadge}
                                            alt="Affiliation"
                                            className={
                                                styles[`physician-bio-hero__affiliation-image`]
                                            }
                                        />
                                        <Text
                                            content={affiliationBadgeLabel}
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                            className="GlobalLinkNoTx"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={styles[`physician-bio-hero__flex-container`]}>
                                {Array.isArray(filteredLanguages) &&
                                    filteredLanguages.length > 0 && (
                                        <div className={styles[`physician-bio-hero__pill`]}>
                                            <Text
                                                content={`Speaks: ${filteredLanguages.join(', ')}`}
                                                tag={Text.TAG.P}
                                                variant={Text.VARIANT.T3}
                                            />
                                        </div>
                                    )}
                                {gender && (
                                    <div className={styles[`physician-bio-hero__pill`]}>
                                        <Text
                                            content={gender}
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                        />
                                    </div>
                                )}
                                {lgbtServices && (
                                    <div className={styles[`physician-bio-hero__pill`]}>
                                        <Text
                                            content="LGBTQ+ Health"
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                        />
                                    </div>
                                )}
                            </div>
                            {insuranceLink && (
                                <div className={styles[`physician-bio-hero__insurance-container`]}>
                                    <Text
                                        content={insurancesText}
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T3}
                                        className={styles[`physician-bio-hero__insurance-text`]}
                                    />
                                    <Button
                                        label={insurancesLinkText}
                                        ariaLabel={insurancesAriaLabel}
                                        href={insuranceLink}
                                        buttonStyle={Button.STYLE.TEXT_SMALL}
                                        className={styles[`physician-bio-hero__insurance-button`]}
                                        analytics={{
                                            componentName: 'fad_bio_hero',
                                            actionLabel: insurancesLinkText,
                                            acceptingNewPatients: acceptingNewPatients,
                                            providerName: providerName,
                                            providerSpecialties: analyticsSpecialties,
                                            providerRating: averageRating,
                                            interactionType: 'fad_bio_cta',
                                        }}
                                    />
                                </div>
                            )}
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 4, md: 6, lg: 4 }}
                            colStart={{ sm: 1, md: 1, lg: 9 }}
                            rowSpan={{ sm: 1, md: 1, lg: 2 }}
                            rowStart={{ sm: 3, md: 3, lg: 1 }}
                            className={styles[`physician-bio-hero__appointment-block`]}
                        >
                            <Theme
                                name={Theme.NAME.PROVIDERS}
                                className={styles[`physician-bio-hero__appointment-block--inner`]}
                            >
                                {!disableAvailabilityMessage && availabilityMessage && (
                                    <p className={styles[`physician-bio-hero__availability-text`]}>
                                        {availabilityMessage.prefix && (
                                            <Text
                                                content={`${availabilityMessage.prefix} `}
                                                tag={Text.TAG.SPAN}
                                                variant={Text.VARIANT.T2}
                                            />
                                        )}
                                        <Text
                                            content={availabilityMessage.value}
                                            tag={Text.TAG.SPAN}
                                            variant={availabilityMessage.valueVariant}
                                        />
                                    </p>
                                )}
                                {/* AHSMKTG-3534: Show Book Appointment when any online availability */}
                                {onlineBooking && hasOnlineAvailability && appointmentUrl && (
                                    <Button
                                        label={scheduleCtaLabel}
                                        ariaLabel={scheduleCtaAriaLabel}
                                        href={appointmentUrl}
                                        buttonStyle={Button.STYLE.PRIMARY}
                                        iconComponent={LazyIconCalendar}
                                        className={styles[`physician-bio-hero__full-width-button`]}
                                        analytics={{
                                            componentName: 'fad_bio_hero',
                                            actionLabel: scheduleCtaLabel,
                                            acceptingNewPatients: acceptingNewPatients,
                                            providerName: providerName,
                                            providerSpecialties: analyticsSpecialties,
                                            providerRating: averageRating,
                                            interactionType: 'fad_bio_primary_cta',
                                        }}
                                    />
                                )}
                                {phoneNumber && (
                                    <Button
                                        label={`Call ${phoneNumber}`}
                                        ariaLabel={`Call ${phoneNumber}`}
                                        href={`tel:${phoneNumber}`}
                                        buttonStyle={
                                            onlineBooking && hasOnlineAvailability && appointmentUrl
                                                ? Button.STYLE.SECONDARY
                                                : Button.STYLE.PRIMARY
                                        }
                                        iconComponent={LazyIconPhone}
                                        className={styles[`physician-bio-hero__full-width-button`]}
                                        analytics={{
                                            componentName: 'fad_bio_hero',
                                            actionLabel: `Call-${phoneNumber}`,
                                            acceptingNewPatients: acceptingNewPatients,
                                            providerName: providerName,
                                            providerSpecialties: analyticsSpecialties,
                                            providerRating: averageRating,
                                            interactionType: 'fad_bio_cta',
                                        }}
                                    />
                                )}
                            </Theme>
                        </GridColumn>
                    </Grid>
                </ContentContainer>
            </section>
        </>
    );
};

PhysicianBioHero.propTypes = {
    /**
     * The image of the provider
     */
    providerImage: PropTypes.shape(Image.propTypes),
    /**
     * Whether the provider is accepting new patients
     */
    acceptingNewPatients: PropTypes.bool,
    /**
     * Whether the provider offers online booking
     * */
    onlineBooking: PropTypes.bool,
    /**
     * Whether the provider offers a Virtual Care specialty service. Gates the Virtual
     * Care indicator, the tiered availability panel and the CTA label swap — all of
     * which sit beneath the `onlineBooking` gate.
     */
    hasVirtualCare: PropTypes.bool,
    /**
     * The visit type of the provider
     */
    visitType: PropTypes.string,
    /**
     * The name of the provider
     */
    providerName: PropTypes.string,
    /**
     * The specialities of the provider
     */
    specialities: PropTypes.arrayOf(PropTypes.string),
    /**
     * The average rating of the provider
     */
    averageRating: PropTypes.number,
    /**
     * The number of reviews for the provider
     */
    numberOfReviews: PropTypes.number,
    /**
     * whether a provider has an Affiliation label = Atlantic Medical Group
     */
    affiliationBadgeLabel: PropTypes.string,
    /**
     * The languages spoken by the provider
     */
    languages: PropTypes.arrayOf(PropTypes.string),
    /**
     * Gender of the Provider
     */
    gender: PropTypes.string,
    /**
     * Whether the provider offers LGBT services
     */
    lgbtServices: PropTypes.bool,
    /**
     * Link to the insurance page
     */
    insuranceLink: PropTypes.string,
    /**
     * provider id
     */
    npi: PropTypes.string,
    /**
     * visit type array
     */
    newPatientAppointmentIds: PropTypes.arrayOf(PropTypes.string),
    /**
     * Label for the schedule CTA. Overrides the non-Virtual-Care default only — a
     * Virtual Care provider's label is swapped internally (see `hasVirtualCare`).
     */
    bookAppointmentText: PropTypes.string,
    /**
     * Accessible name for the schedule CTA. When omitted, it is derived from the CTA's
     * visible label and the provider's name.
     */
    bookAppointmentAriaLabel: PropTypes.string,
    /**
     * Label that prefixes the availability date/time, e.g. `'Available as soon as: '`.
     */
    availabilityText: PropTypes.string,
    /**
     * The appointment URL
     */
    appointmentUrl: PropTypes.string,
    /**
     * The phone number of the provider
     */
    phoneNumber: PropTypes.string,
    /**
     * AHSMKTG-3534: Has appointments within 180 days. Gates the availability message —
     * when false, no availability message renders regardless of `onlineBooking`,
     * `hasVirtualCare`, or `category`. Also feeds `hasOnlineAvailability` below.
     */
    availableSoonOnline: PropTypes.bool,
    /**
     * AHSMKTG-3534: Has appointments within 545 days (shows Book Appointment CTA)
     */
    availableOnline: PropTypes.bool,
    /**
     * AHSMKTG-3534: ISO date string for the provider's first known available appointment.
     * Used as the start date for the availability API window.
     */
    firstAvailabilityDate: PropTypes.string,
    /**
     * AHSMKTG-3534: ISO date string for the provider's next known available appointment.
     */
    nextAvailabilityDate: PropTypes.string,
    /**
     * When true, suppresses the availabilityMessage render block (lines 490–502).
     * The schedule CTA and phone CTA are unaffected.
     */
    disableAvailabilityMessage: PropTypes.bool,
};

export default PhysicianBioHero;
